import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * Pins #header-component to its exact current on-screen position with an
 * inline style, for as long as a dialog is open.
 *
 * Mirrors the same fix in header-drawer.js (see that file for the full
 * rationale on why an inline style is used instead of another CSS rule).
 * Needed here too: closeDialog() below explicitly calls scrollTo() to
 * restore the pre-open scroll position once the dialog closes, which is a
 * genuine, deliberate scroll action — exactly what the header's own
 * scroll-driven sticky logic is designed to react to. theme-drawer.js
 * (used by the cart drawer) has no equivalent restoration call, which is
 * why cart never disturbed the header while this dialog-based path did.
 *
 * Scoped to mobile only (matching every other header/drawer fix in this
 * theme) — desktop search behaves completely differently, and pinning the
 * header there was contributing to a visible layout jump rather than
 * preventing one.
 * @returns {HTMLElement | null} The header element, if found and locked.
 */
function lockHeaderPosition() {
  if (window.innerWidth >= 750) return null;

  const header = /** @type {HTMLElement | null} */ (document.querySelector('#header-component'));
  if (!header) return null;

  header.style.setProperty('transition', 'none', 'important');
  void header.offsetHeight;

  const rect = header.getBoundingClientRect();
  const section = header.closest('.header-section');

  // Transparent headers are absolutely positioned (see .header[transparent]
  // in sections/header.liquid) and were never occupying flow height to
  // begin with, so reserving space for them here serves no purpose. On
  // pages where a shopify-section (e.g. the homepage hero) sits
  // immediately after #header-group, that reservation was the direct
  // cause of that content visibly shifting down for as long as the dialog
  // stayed open. Sticky/opaque headers genuinely need this reservation, to
  // stop whatever follows from jumping up into the space the header used
  // to occupy once it goes fixed — that path is untouched.
  if (section instanceof HTMLElement && !header.hasAttribute('transparent')) {
    section.style.setProperty('min-height', `${Math.round(rect.height)}px`);
  }

  header.style.setProperty('position', 'fixed');
  header.style.setProperty('top', `${Math.round(rect.top)}px`);
  header.style.setProperty('left', '0');
  header.style.setProperty('right', '0');

  return header;
}

/**
 * Releases the position lock applied by lockHeaderPosition.
 */
function unlockHeaderPosition() {
  if (window.innerWidth >= 750) return;

  const header = /** @type {HTMLElement | null} */ (document.querySelector('#header-component'));
  if (!header) return;

  const section = header.closest('.header-section');

  header.style.removeProperty('position');
  header.style.removeProperty('top');
  header.style.removeProperty('left');
  header.style.removeProperty('right');

  if (section instanceof HTMLElement) {
    section.style.removeProperty('min-height');
  }

  // Keep the transition suppressed briefly past the unlock too, for the
  // same reason as header-drawer.js's #suppressHeaderTransition: a single
  // frame isn't enough margin against other things touching the header
  // right as this settles.
  window.setTimeout(() => {
    header.style.removeProperty('transition');
  }, 350);
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();
    lockHeaderPosition();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      // showModal() auto-focuses the dialog's first focusable element
      // (typically the search input) as part of native browser behavior,
      // and browsers scroll that element into view by default unless told
      // not to — the same category of bug already fixed for the mobile
      // menu's trapFocus() call, just a different, browser-internal call
      // site that can't be intercepted directly. Re-focusing the same
      // element immediately with preventScroll cancels out any
      // scroll-into-view the initial native focus triggered.
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && dialog.contains(active)) {
          active.blur();
          active.focus({ preventScroll: true });
        }
      });

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    unlockHeaderPosition();

    // Native dialogs restore focus to their opener after closing. For the
    // desktop search modal that would leave the header in its solid
    // :focus-within/hover appearance. Suppress only that stale state until the
    // pointer genuinely leaves the header; normal hover behavior then resumes.
    if (this.id === 'search-modal') {
      requestAnimationFrame(() => {
        const header = document.querySelector('#header-component');
        if (!header) return;

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }

        header.classList.add('blm-search-just-closed');
        header.addEventListener(
          'pointerleave',
          () => header.classList.remove('blm-search-just-closed'),
          { once: true }
        );
      });
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
